import { useTranslation } from 'react-i18next';
import { useCycleStore } from '../../stores/cycleStore';
import { Card } from '../common';
import { useCurrency } from '../../lib/utils/currency';
import type { ProductionCycle } from '../../types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ComparisonChartsProps {
  selectedIds: string[];
}

export function ComparisonCharts({ selectedIds }: ComparisonChartsProps) {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { getCycle } = useCycleStore();
  
  const selectedCycles = selectedIds
    .map((id) => getCycle(id))
    .filter((cycle): cycle is ProductionCycle => cycle !== undefined);

  if (selectedCycles.length === 0) {
    return null;
  }

  // Prepare data for charts
  const performanceData = selectedCycles.map((cycle) => ({
    name: cycle.name,
    FCR: parseFloat(cycle.performance.fcr.toFixed(3)),
    EPEF: parseFloat(cycle.performance.epef.toFixed(2)),
    [t('comparison.survival')]: parseFloat(cycle.performance.survivalRate.toFixed(2)),
  }));

  const financialData = selectedCycles.map((cycle) => ({
    name: cycle.name,
    [t('comparison.grossRevenue')]: cycle.revenue.grossRevenue,
    [t('comparison.expenses')]: cycle.netProfit.totalExpenses,
    [t('comparison.netProfit')]: cycle.netProfit.netProfit,
  }));

  const formatCurrency = (value: number) => {
    return `${value.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} ${symbol}`;
  };

  return (
    <div className="space-y-6">
      {/* Performance Comparison Chart */}
      <Card title={t('comparison.performanceComparison')}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={performanceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="FCR" fill="#ef4444" name="FCR" />
            <Bar dataKey="EPEF" fill="#3b82f6" name="EPEF" />
            <Bar dataKey={t('comparison.survival')} fill="#22c55e" name={t('comparison.survival')} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Financial Comparison Chart */}
      <Card title={t('comparison.financialComparison')}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={financialData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={formatCurrency} />
            <Tooltip formatter={(value) => formatCurrency(value as number)} />
            <Legend />
            <Bar dataKey={t('comparison.grossRevenue')} fill="#22c55e" name={t('comparison.grossRevenue')} />
            <Bar dataKey={t('comparison.expenses')} fill="#ef4444" name={t('comparison.expenses')} />
            <Bar dataKey={t('comparison.netProfit')} fill="#3b82f6" name={t('comparison.netProfit')} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
